/**
 * Slide-in cart drawer.
 *
 * Opens on add-to-cart and on the header cart icon, and renders from /cart.js.
 * Quantity changes, removals and one-tap upsells go through the Ajax cart API
 * and repaint in place.
 */
(function () {
  'use strict';

  var root, panel, itemsEl, countEl, totalEl, footEl, emptyEl;
  var bumpEl, bumpText, bumpBtn, upsEl, savedEl, savedAmt;
  var lastFocused = null;
  var format, threshold, bundleId, tiers;
  var busy = false;

  document.addEventListener('DOMContentLoaded', function () {
    root = document.querySelector('[data-nova-drawer]');
    if (!root) return;

    panel = root.querySelector('[data-nova-drawer-panel]');
    itemsEl = root.querySelector('[data-nova-drawer-items]');
    countEl = root.querySelector('[data-nova-drawer-count]');
    totalEl = root.querySelector('[data-nova-drawer-total]');
    footEl = root.querySelector('[data-nova-drawer-foot]');
    emptyEl = root.querySelector('[data-nova-drawer-empty]');
    bumpEl = root.querySelector('[data-nova-drawer-bump]');
    bumpText = root.querySelector('[data-nova-drawer-bump-text]');
    bumpBtn = root.querySelector('[data-nova-drawer-bump-btn]');
    upsEl = root.querySelector('[data-nova-drawer-ups]');
    savedEl = root.querySelector('[data-nova-drawer-saved]');
    savedAmt = root.querySelector('[data-nova-drawer-saved-amt]');

    format = root.dataset.moneyFormat;
    threshold = Number(root.dataset.threshold) || 0;
    bundleId = Number(root.dataset.bundle) || 0;
    tiers = parseTiers(root.dataset.tiers);

    root.querySelectorAll('[data-nova-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (root.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') trapFocus(e);
    });

    // Header cart icon opens the drawer rather than navigating.
    document.querySelectorAll('.nova-header__cart').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        openAndRefresh();
      });
    });

    // The buy button fires this once the items are in.
    document.addEventListener('nova:cart-updated', openAndRefresh);

    itemsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-nova-qty], [data-nova-remove]');
      if (!btn) return;
      var key = btn.dataset.key;
      change(key, btn.hasAttribute('data-nova-remove') ? 0 : Number(btn.dataset.qty));
    });

    if (bumpBtn) {
      bumpBtn.addEventListener('click', function () {
        var key = bumpBtn.dataset.key;
        var qty = Number(bumpBtn.dataset.qty);
        if (key && qty) change(key, qty, bumpBtn);
      });
    }

    if (upsEl) {
      upsEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-nova-up-add]');
        if (!btn) return;
        post('cart/add.js', { items: [{ id: Number(btn.dataset.novaUpAdd), quantity: 1 }] }, btn)
          .then(refresh);
      });
    }
  });

  function parseTiers(raw) {
    return String(raw || '')
      .split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        return { qty: parseInt(bits[0], 10), pct: parseFloat(bits[1]) };
      })
      .filter(function (t) { return t.qty > 1 && t.pct > 0; })
      .sort(function (a, b) { return a.qty - b.qty; });
  }

  function openAndRefresh() {
    open();
    refresh();
  }

  function open() {
    if (!root.hidden && root.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    root.hidden = false;
    // Next frame so the transition actually runs.
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      document.documentElement.classList.add('nova-drawer-open');
      if (panel) panel.focus();
    });
  }

  function close() {
    root.classList.remove('is-open');
    document.documentElement.classList.remove('nova-drawer-open');
    window.setTimeout(function () {
      root.hidden = true;
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }, 280);
  }

  function refresh() {
    return fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () { /* leave the last painted state */ });
  }

  function change(key, quantity, trigger) {
    return post('cart/change.js', { id: key, quantity: quantity }, trigger).then(function (cart) {
      if (cart && cart.items) render(cart);
    });
  }

  function post(path, body, trigger) {
    if (busy) return Promise.resolve(null);
    busy = true;
    if (trigger) trigger.disabled = true;
    return fetch(route(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; })
      .then(function (res) {
        busy = false;
        if (trigger) trigger.disabled = false;
        return res;
      });
  }

  function render(cart) {
    if (!cart || !itemsEl) return;

    countEl.textContent = cart.item_count ? '(' + cart.item_count + ')' : '';
    totalEl.textContent = money(cart.total_price);

    document.querySelectorAll('[data-nova-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    var empty = cart.item_count === 0;
    emptyEl.hidden = !empty;
    footEl.hidden = empty;

    itemsEl.innerHTML = cart.items.map(function (item) {
      var img = item.image
        ? '<img src="' + resize(item.image, 160) + '" alt="" width="68" height="68" loading="lazy">'
        : '<span class="nova-drawer__noimg" aria-hidden="true"></span>';

      var discounts = (item.line_level_discount_allocations || [])
        .map(function (d) { return d.discount_application && d.discount_application.title; })
        .filter(Boolean);

      var struck = item.original_line_price > item.final_line_price
        ? '<s>' + money(item.original_line_price) + '</s>' : '';

      return '' +
        '<li class="nova-drawer__item">' +
          '<span class="nova-drawer__thumb">' + img + '</span>' +
          '<span class="nova-drawer__meta">' +
            '<strong>' + escapeHtml(item.product_title) + '</strong>' +
            (item.variant_title && item.variant_title !== 'Default Title'
              ? '<small>' + escapeHtml(item.variant_title) + '</small>' : '') +
            (discounts.length ? '<em>' + escapeHtml(discounts.join(', ')) + '</em>' : '') +
            '<span class="nova-drawer__qty">' +
              '<button type="button" data-nova-qty data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">&minus;</button>' +
              '<b>' + item.quantity + '</b>' +
              '<button type="button" data-nova-qty data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>' +
            '</span>' +
          '</span>' +
          '<span class="nova-drawer__line">' +
            '<strong>' + money(item.final_line_price) + '</strong>' + struck +
            '<button type="button" data-nova-remove data-key="' + item.key + '">Remove</button>' +
          '</span>' +
        '</li>';
    }).join('');

    paintSavings(cart);
    paintShipping(cart);
    paintBump(cart);
    paintUpsells(cart);
  }

  function paintSavings(cart) {
    if (!savedEl) return;
    var saved = cart.total_discount || 0;
    savedEl.hidden = saved <= 0;
    if (saved > 0) savedAmt.textContent = '−' + money(saved);
  }

  function paintShipping(cart) {
    var wrap = root.querySelector('[data-nova-drawer-ship]');
    if (!wrap) return;
    if (!threshold || cart.item_count === 0) { wrap.hidden = true; return; }

    var text = root.querySelector('[data-nova-drawer-ship-text]');
    var bar = root.querySelector('[data-nova-drawer-ship-bar]');
    var left = threshold - cart.total_price;

    wrap.hidden = false;
    if (left > 0) {
      text.innerHTML = 'You are <b>' + money(left) + '</b> away from free shipping';
      bar.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
    } else {
      text.innerHTML = '<b>Free shipping unlocked</b> — nice shot.';
      bar.style.width = '100%';
    }
  }

  // Suggest the next bundle tier for the main product, e.g. "Add 1 more and
  // save 10% on every projector". Relies on matching automatic discounts.
  function paintBump(cart) {
    if (!bumpEl) return;
    var line = bundleId && cart.items.filter(function (i) { return i.product_id === bundleId; })[0];
    var next = line && tiers.filter(function (t) { return t.qty > line.quantity; })[0];

    if (!line || !next) { bumpEl.hidden = true; return; }

    var more = next.qty - line.quantity;
    bumpText.innerHTML = 'Add <b>' + more + ' more</b> and save <b>' + next.pct + '%</b> on every ' +
      escapeHtml(shortName(line.product_title)) + '.';
    bumpBtn.dataset.key = line.key;
    bumpBtn.dataset.qty = next.qty;
    bumpBtn.textContent = '+ ' + more;
    bumpEl.hidden = false;
  }

  function paintUpsells(cart) {
    if (!upsEl) return;
    var inCart = {};
    cart.items.forEach(function (i) { inCart[i.product_id] = true; });

    var shown = 0;
    upsEl.querySelectorAll('[data-nova-up]').forEach(function (row) {
      var hide = !!inCart[Number(row.dataset.product)] || cart.item_count === 0;
      row.hidden = hide;
      if (!hide) shown++;
    });
    upsEl.hidden = shown === 0;
  }

  function shortName(title) {
    return String(title).replace(/[™®]/g, '').trim() || 'item';
  }

  function route(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + path;
  }

  function resize(url, width) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function money(cents) {
    return window.novaMoney ? window.novaMoney(cents, format) : '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function trapFocus(e) {
    var focusables = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
})();
